import React, { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, FileText, RefreshCcw, ScrollText } from "lucide-react";
import api from "../../lib/api";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { adminNav } from "../../components/shared/navConfig";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { toast } from "sonner";

const KIND_META = {
  kyc_review:    { label: "KYC review",         icon: ShieldCheck, tone: "text-blue-400" },
  gst_review:    { label: "GST review",         icon: FileText,    tone: "text-blue-400" },
  task_review:   { label: "Task review",        icon: FileText,    tone: "text-purple-400" },
  suspend:       { label: "User suspended",     icon: ShieldOff,   tone: "text-rose-400" },
  unsuspend:     { label: "User reinstated",    icon: ShieldCheck, tone: "text-emerald-400" },
  internal_note: { label: "Internal note",      icon: ScrollText,  tone: "text-zinc-300" },
};

const DECISION_STATUS = {
  approve: "Approved", reject: "Rejected", request_resubmit: "Pending",
  request_changes: "Revision Required", note: "Verified",
  suspend: "Rejected", unsuspend: "Approved",
};

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export default function AdminAuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/audit"); setRows(data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <DashboardShell nav={adminNav} title="Audit logs" subtitle="Every admin decision and internal note across the platform">
      <div className="card-surface p-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="text-xs text-zinc-500">{rows.length} entries</div>
          <button onClick={load} data-testid="audit-refresh-btn" className="btn-ghost ml-auto px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1.5"><RefreshCcw size={12}/> Refresh</button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-zinc-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-zinc-500">No audit events yet.</div>
        ) : (
          <ol className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-px" style={{ background: "var(--border)" }}/>
            {rows.map((r, i) => {
              const meta = KIND_META[r.kind] || { label: r.kind, icon: FileText, tone: "text-zinc-300" };
              const Icon = meta.icon;
              return (
                <li key={i} data-testid={`audit-row-${i}`} className="relative pl-12 pb-6 last:pb-0">
                  <div className="absolute left-0 top-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--surface-2)", border: "1px solid #27272A" }}>
                    <Icon size={14} className={meta.tone}/>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-white">{meta.label}</span>
                        <StatusBadge status={DECISION_STATUS[r.decision] || "Pending"}/>
                        {r.task_id && <span className="text-[10px] font-mono text-zinc-500">{r.task_id}</span>}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        Target: <span className="text-zinc-300">{r.target_name || r.target_email || r.target_user_id}</span>
                        {r.note && <> · <span className="text-zinc-400">"{r.note}"</span></>}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-600 mt-1">{fmt(r.created_at)}</div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </DashboardShell>
  );
}
